import './ItemTeaser.scss';
import React, { type ReactNode, useDebugValue, useEffect, useRef, useState } from 'react';
import { pipe, tryCatch, isEmpty, isNil, F } from 'ramda';
import cx from 'classnames'
import Loader from '../loader/Loader';
import IconButton from '../iconButton/IconButton';
import ErrorMessage from '../errorMessage/ErrorMessage';

export type ItemTeaserProps = {
    alt?: string,
    icon?: string,
    onLoad?: () => void,
    overlayTag?: ReactNode,
    src?: string,
    isLoading?: boolean,
    type?: 'video' | 'image',
    videoSource?: string,
}

const ItemTeaser = ({
    alt,
    icon,
    onLoad = () => null,
    overlayTag,
    src,
    isLoading,
    type,
    videoSource
}: ItemTeaserProps) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    useDebugValue(`Error: ${error}`)
    const onReady = () => setLoading(false);
    const onError = () => {
        type === 'video' && setError('')
        type === 'image' && setError('')
    };
    const handleLoad = pipe(onReady, onLoad);
    const handleError = pipe(onError, onReady, onLoad);

    useEffect(() => {
        if (isEmpty(src)) handleLoad();
    }, [src]);

    /**
     * Loading can be controlled via Prop from ItemCard
     * Or locally by setting it with onLoad call back
     */
    useEffect(() => {
        setLoading(isLoading || false)
    }, [isLoading]);

    const [isVisible, setIsVisible] = useState(true)
    const hideIcon =  () => setIsVisible(false);
    const showIcon =  () => setIsVisible(true);

    const videoElementRef = useRef<HTMLVideoElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const toggleIsPlaying = ()  => setIsPlaying(!isPlaying)
    const stopAllVideos = () => document.querySelectorAll('video').forEach(video => video.pause())

    const handlePlaybackError = () => {
        if (videoElementRef?.current?.error?.code === 4) return setError('Video not found.')
        setError('')
    }

    const playVideo = () => { videoSource && videoElementRef.current?.play() }
    const handlePlayVideo = pipe(
        stopAllVideos,
        hideIcon,
        toggleIsPlaying,
        tryCatch(playVideo, handlePlaybackError)
    )

    const pauseVideo = () => { videoElementRef.current?.pause() }
    const handlePauseVideo = pipe(
        toggleIsPlaying,
        showIcon,
        tryCatch(pauseVideo, handleError)
    )

  
    const renderVideoElement = () => (
        <video
            className="b-item-teaser__video-element"
            disablePictureInPicture
            muted
            onClick={handlePauseVideo}
            onEnded={showIcon}
            onLoadedMetadata={handleLoad}
            onError={handlePlaybackError}
            autoPlay
            ref={videoElementRef}
            src={videoSource}
        />
    )

    const renderImageElement = () => (
        <img
            className={cx('b-item-teaser__image', {'is-hidden': !src || !isNil(error) })}
            alt={alt}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
            onClick={videoSource ? handlePlayVideo : undefined}
            src={src || undefined}
            style={{ cursor: videoSource && 'pointer' }}
        />
    )

    return (
        <div className="b-item-teaser">
            <Loader loading={loading} />
            {overlayTag}
            {error && (
                <div className="b-item-teaser__error-container">
                    <ErrorMessage className="error-message" message={error} />
                </div>
            )}
            {isPlaying
                ? renderVideoElement()
                : renderImageElement()
            }
            {isVisible && !error && icon && (
                <IconButton
                    name="item-teaser-icon"
                    className="b-item-teaser__icon"
                    icon={icon}
                    size="large"
                />
            )}
        </div>
    );
};

export default ItemTeaser;
